/**
 * MP4 export: renders every frame through the same compositor as the preview (into an
 * OffscreenCanvas), encodes with WebCodecs (H.264/HEVC + AAC/Opus) and muxes with mediabunny.
 * Source video is decoded frame-accurately from the full-quality master files (URLs or Blobs).
 *
 * Memory stays constant regardless of the export length: the MP4 is streamed to its destination
 * (with space reserved up front for the index, so the file still starts playing instantly), and
 * the audio mix is rendered in short windows interleaved with the video.
 */
import {
  forEachLayer,
  fpsValue,
  resolveAudio,
  resolveFrame,
  sequenceDuration,
  type FrameDescription,
  type LayerNode,
  type Project,
} from '@ve/core';
import {
  AudioBufferSource,
  BufferTarget,
  CanvasSource,
  Mp4OutputFormat,
  Output,
  StreamTarget,
  VideoSample,
  getFirstEncodableAudioCodec,
  getFirstEncodableVideoCodec,
  type AudioCodec,
  type VideoCodec,
} from 'mediabunny';
import { Compositor, type LayerTexture, type TextureProvider } from '../compositor/compositor.ts';
import { ImageCache } from '../media/image-cache.ts';
import { logicalSize } from '../media/logical-size.ts';
import type { MediaResolver } from '../types.ts';
import { DEFAULT_AUDIO_WINDOW_SECONDS, WindowedAudioMixer } from './audio-mixer.ts';
import { estimateAudioPackets, estimateVideoPackets, totalSamples } from './audio-windows.ts';
import { guardStream, type ExportDestination, type GuardedStream } from './destination.ts';
import { MediaInputs, SequentialFrameReader } from './frame-reader.ts';

export type { ExportDestination } from './destination.ts';

export interface ExportOptions {
  project: Project;
  sequenceId: string;
  resolveMedia: MediaResolver;
  /** Frame range `[start, end)`; defaults to the whole sequence. */
  range?: { start: number; end: number };
  /** Output size; defaults to the project size. Rounded to even numbers. */
  width?: number;
  height?: number;
  /** Video bitrate in bits/s. Default scales with resolution (~12 Mbit/s at 1080p30). */
  videoBitrate?: number;
  /** Audio bitrate in bits/s. Default 192 kbit/s. */
  audioBitrate?: number;
  /** Preferred codec; falls back to H.264 if unsupported. Default `avc`. */
  codec?: 'avc' | 'hevc';
  /** Seconds between key frames. Default 2. */
  keyFrameInterval?: number;
  fileName?: string;
  /**
   * Where the MP4 goes. Use `{ kind: 'stream', writable }` (e.g. a FileSystemWritableFileStream)
   * for constant memory use. Defaults to `{ kind: 'memory' }` (whole file kept in memory).
   */
  destination?: ExportDestination;
  /** Length of the audio mixing windows, seconds. Default 10. */
  audioWindowSeconds?: number;
  onProgress?: (progress: number, frame: number) => void;
  signal?: AbortSignal;
}

export interface ExportResult {
  /** Only for the `memory` destination. */
  blob?: Blob;
  /** Size of the produced file in bytes. */
  bytesWritten: number;
  mimeType: string;
  fileName: string;
  videoCodec: VideoCodec;
  audioCodec: AudioCodec | null;
  /** Wall-clock seconds the export took. */
  elapsed: number;
}

/** Audio is fed to the muxer this far ahead of the video to keep tracks interleaved. */
const AUDIO_LEAD_SECONDS = 1;
/** Frame readers unused for this many seconds of output are closed. */
const READER_IDLE_SECONDS = 2;

export function defaultVideoBitrate(width: number, height: number, fps: number): number {
  return Math.round(width * height * fps * 0.2);
}

function even(n: number): number {
  return Math.max(2, Math.round(n / 2) * 2);
}

function abortError(): DOMException {
  return new DOMException('Export was cancelled', 'AbortError');
}

export async function exportVideo(options: ExportOptions): Promise<ExportResult> {
  const startedAt = performance.now();
  const { project, sequenceId, resolveMedia, signal } = options;
  if (!project.sequences[sequenceId]) throw new Error(`Unknown sequence ${sequenceId}`);
  const fps = fpsValue(project.settings.fps);
  const total = sequenceDuration(project, sequenceId);
  const range = options.range ?? { start: 0, end: total };
  const start = Math.max(0, Math.floor(range.start));
  const end = Math.min(Math.max(start, Math.floor(range.end)), Math.max(total, start));
  if (end <= start) throw new Error('Nothing to export: the selected range is empty');
  const width = even(options.width ?? project.settings.width);
  const height = even(options.height ?? project.settings.height);
  const videoBitrate = options.videoBitrate ?? defaultVideoBitrate(width, height, fps);
  const audioBitrate = options.audioBitrate ?? 192_000;
  const sampleRate = project.settings.sampleRate;

  const videoCodec = await getFirstEncodableVideoCodec(options.codec === 'hevc' ? ['hevc', 'avc'] : ['avc'], {
    width,
    height,
    bitrate: videoBitrate,
    frameRate: fps,
  });
  if (!videoCodec) throw new Error(`This browser cannot encode H.264 video at ${width}×${height}`);

  const startSec = start / fps;
  const endSec = end / fps;
  const segments = resolveAudio(project, sequenceId).filter(
    (s) => s.start < endSec && s.start + s.duration > startSec,
  );
  const audioCodec =
    segments.length > 0
      ? await getFirstEncodableAudioCodec(['aac', 'opus'], {
          numberOfChannels: 2,
          sampleRate,
          bitrate: audioBitrate,
        })
      : null;
  if (segments.length > 0 && !audioCodec) throw new Error('This browser cannot encode AAC or Opus audio');

  const canvas = new OffscreenCanvas(width, height);
  const compositor = new Compositor(canvas, { preserveDrawingBuffer: true, highQuality: true });
  const inputs = new MediaInputs();
  const readers = new Map<string, SequentialFrameReader>();
  const images = new ImageCache();
  const destination = options.destination ?? { kind: 'memory' };
  const guarded: GuardedStream | null =
    destination.kind === 'stream' ? guardStream(destination.writable) : null;
  const output = new Output({
    // Streams reserve space for the index at the start ("fast start" without buffering the file).
    format: new Mp4OutputFormat({ fastStart: guarded ? 'reserve' : 'in-memory' }),
    target: guarded ? new StreamTarget(guarded.stream, { chunked: true }) : new BufferTarget(),
  });
  const mixer = audioCodec
    ? new WindowedAudioMixer(
        project,
        segments,
        resolveMedia,
        startSec,
        endSec,
        sampleRate,
        options.audioWindowSeconds ?? DEFAULT_AUDIO_WINDOW_SECONDS,
      )
    : null;

  try {
    const videoSource = new CanvasSource(canvas, {
      codec: videoCodec,
      bitrate: videoBitrate,
      keyFrameInterval: options.keyFrameInterval ?? 2,
    });
    output.addVideoTrack(videoSource, {
      frameRate: fps,
      maximumPacketCount: estimateVideoPackets(end - start),
    });
    let audioSource: AudioBufferSource | null = null;
    if (audioCodec) {
      audioSource = new AudioBufferSource({ codec: audioCodec, bitrate: audioBitrate });
      const audioSeconds = totalSamples(startSec, endSec, sampleRate) / sampleRate;
      output.addAudioTrack(audioSource, {
        maximumPacketCount: estimateAudioPackets(audioSeconds, sampleRate, audioCodec),
      });
    }
    await output.start();
    options.onProgress?.(0.01, start);

    // Feed audio windows slightly ahead of the video to keep the tracks interleaved.
    let nextWindow = 0;
    const feedAudio = async (until: number) => {
      if (!mixer || !audioSource) return;
      while (nextWindow < mixer.windows.length && mixer.windows[nextWindow]!.from - startSec < until) {
        if (signal?.aborted) throw abortError();
        const pcm = await mixer.render(mixer.windows[nextWindow]!);
        await audioSource.add(pcm);
        nextWindow++;
      }
    };

    const prepared = new Map<string, LayerTexture>();
    const provider: TextureProvider = { getTexture: (layer) => prepared.get(layer.key) ?? null };

    for (let frame = start; frame < end; frame++) {
      if (signal?.aborted) throw abortError();
      const t = (frame - start) / fps;
      await feedAudio(t + AUDIO_LEAD_SECONDS);
      const desc = resolveFrame(project, sequenceId, frame);
      await prepareFrame(desc, frame / fps, project, resolveMedia, inputs, readers, images, prepared);
      compositor.render(desc, provider);
      closeFrames(prepared);
      await videoSource.add(t, 1 / fps);
      await closeIdleReaders(readers, frame / fps);
      options.onProgress?.(0.01 + 0.98 * ((frame - start + 1) / (end - start)), frame);
    }
    await feedAudio(Infinity);
    videoSource.close();
    audioSource?.close();
    guarded?.allowCommit();
    await output.finalize();
    const mimeType = await output.getMimeType();
    let blob: Blob | undefined;
    let bytesWritten: number;
    if (guarded) {
      bytesWritten = guarded.bytesWritten;
    } else {
      const buffer = (output.target as BufferTarget).buffer;
      if (!buffer) throw new Error('Export produced no data');
      blob = new Blob([buffer], { type: 'video/mp4' });
      bytesWritten = buffer.byteLength;
    }
    options.onProgress?.(1, end);
    return {
      blob,
      bytesWritten,
      mimeType,
      fileName: options.fileName ?? `${sanitizeFileName(project.name)}.mp4`,
      videoCodec,
      audioCodec,
      elapsed: (performance.now() - startedAt) / 1000,
    };
  } catch (err) {
    if (output.state !== 'finalized' && output.state !== 'canceled')
      await output.cancel().catch(() => undefined);
    // Discard the partial file (a FileSystemWritableFileStream only commits on close).
    await guarded?.abort(err);
    throw err;
  } finally {
    await mixer?.dispose().catch(() => undefined);
    await Promise.all([...readers.values()].map((r) => r.close().catch(() => undefined)));
    inputs.dispose();
    images.clear();
    compositor.dispose();
  }
}

/** Loads all pixels needed for a frame into `prepared` (keyed by layer key). */
async function prepareFrame(
  desc: FrameDescription,
  timeSec: number,
  project: Project,
  resolveMedia: MediaResolver,
  inputs: MediaInputs,
  readers: Map<string, SequentialFrameReader>,
  images: ImageCache,
  prepared: Map<string, LayerTexture>,
): Promise<void> {
  prepared.clear();
  const layers: LayerNode[] = [];
  forEachLayer(desc.layers, (l) => layers.push(l));
  await Promise.all(
    layers.map(async (layer) => {
      const source = layer.source;
      if (source.type !== 'video' && source.type !== 'image') return;
      const master = resolveMedia(source.mediaId)?.master;
      if (!master)
        throw new Error(`Media "${project.media[source.mediaId]?.name ?? source.mediaId}" is not available`);
      const asset = project.media[source.mediaId];
      if (source.type === 'image') {
        const img = await images.load(master);
        prepared.set(layer.key, {
          source: img.bitmap,
          ...logicalSize(asset, img.width, img.height),
          cacheKey: `image:${img.key}`,
          version: img.key,
        });
        return;
      }
      let reader = readers.get(layer.key);
      if (!reader) {
        reader = await SequentialFrameReader.create(await inputs.videoTrack(master));
        readers.set(layer.key, reader);
      }
      reader.lastUsed = timeSec;
      const decoded = await reader.frameAt(source.time);
      if (!decoded) return;
      if (decoded instanceof VideoSample) {
        // Uploaded raw (rotation still to apply), so the compositor sees the original HDR signal.
        prepared.set(layer.key, {
          source: decoded.toVideoFrame(),
          ...logicalSize(asset, decoded.displayWidth, decoded.displayHeight),
          transfer: reader.transfer,
          rotation: decoded.rotation,
        });
        return;
      }
      const { canvas } = decoded;
      prepared.set(layer.key, { source: canvas, ...logicalSize(asset, canvas.width, canvas.height) });
    }),
  );
}

/** Frames cloned for upload by `prepareFrame` hold decoder memory until closed. */
function closeFrames(prepared: Map<string, LayerTexture>): void {
  for (const t of prepared.values()) if (t.source instanceof VideoFrame) t.source.close();
}

async function closeIdleReaders(readers: Map<string, SequentialFrameReader>, timeSec: number): Promise<void> {
  for (const [key, reader] of readers) {
    if (timeSec - reader.lastUsed > READER_IDLE_SECONDS) {
      readers.delete(key);
      await reader.close();
    }
  }
}

export function sanitizeFileName(name: string): string {
  const cleaned = name.replace(/[^\p{L}\p{N} ._-]+/gu, '').trim();
  return cleaned.length > 0 ? cleaned : 'export';
}
